import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import { EmbeddedRoute } from 'components/EmbeddedRoute';
import { ToastProvider } from './components/ui/toast';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ThemeProvider } from './contexts/ThemeContext';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { setUser } from './store/authSlice';
import { authService } from './api';

import Loader from 'components/ui/Loader';

const LoginPage = React.lazy(() => import('./pages/LoginPage/LoginPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage/DashboardPage'));
const DashboardPageV2 = React.lazy(() => import('./pages/DashboardPage/DashboardPageV2'));
const LogsPage = React.lazy(() => import('./pages/LogsPage/LogsPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage/ProfilePage'));

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  useEffect(() => {
    // Listen for storage changes to update authentication state
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_token') {
        const user = authService.getCurrentUser();
        dispatch(setUser(user));
      }
    };

    // Listen for custom auth events
    const handleAuthChange = () => {
      const user = authService.getCurrentUser();
      dispatch(setUser(user));
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-changed', handleAuthChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-changed', handleAuthChange);
    };
  }, [dispatch]);

  return (
    <ErrorBoundary>
      <ToastProvider>
        <Router>
          <ThemeProvider>
            <div className="h-screen bg-background">
              <Suspense fallback={<Loader fullScreen={true} />}>
                <Routes>
                  {isAuthenticated ? (
                    <>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                      <Route path="/logs" element={<ProtectedRoute><LogsPage /></ProtectedRoute>} />
                      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                    </>
                  ) : (
                    <>
                      <Route path="/" element={<LoginPage />} />
                      <Route path="/embedded-logs" element={<LogsPage />} />
                      <Route path="/health-assistant" element={<EmbeddedRoute><DashboardPageV2 /></EmbeddedRoute>} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </>
                  )}
                </Routes>
              </Suspense>
            </div>
          </ThemeProvider>
        </Router>
      </ToastProvider>
    </ErrorBoundary>
  );
};

export default App;